import { EnumAyaEventType } from '../enums/aya-event-type.enum';
import { EnumRunMode } from '../enums/run-mode.enum';
import { EnumRunPhase } from '../enums/run-phase.enum';
import { AYA_STREAM_PROTOCOL, AyaStreamProtocol } from './stream-message-request.interface';

/** Só estes fecham a execução. `message.completed`, fase, tool e EOF não fecham. */
export const TERMINAL_EVENT_TYPES: ReadonlyArray<string> = [
  EnumAyaEventType.RUN_COMPLETED,
  EnumAyaEventType.RUN_FAILED,
  EnumAyaEventType.RUN_INTERRUPTED,
  EnumAyaEventType.RUN_CANCELLED
];

export type ArtifactKind = 'attachment' | 'user_attachment' | 'chart' | 'reference';

export type ArtifactPlacement = 'user' | 'agent';

/* eslint-disable @typescript-eslint/naming-convention */
export interface IAyaEnvelopeRaw {
  protocol: AyaStreamProtocol;
  session_id: string;
  run_id: string;
  message_id: string;
  tool_call_id: string | null;
  seq: number;
  timestamp: string;
  type: string;
  payload: Record<string, unknown>;
}

export interface IRunStartedPayloadRaw {
  mode: EnumRunMode;
  response_message_id: string;
  resumed_interrupt_id?: string;
}

export interface IRunPhasePayloadRaw {
  phase: EnumRunPhase;
}

export interface IMessageDeltaPayloadRaw {
  message_id: string;
  text: string;
}

export interface IMessageCompletedPayloadRaw {
  message_id: string;
  text: string;
  finish_reason?: string;
}

export interface IToolStartedPayloadRaw {
  tool_key: string;
  label: string;
  parent_subagent_id?: string | null;
  arguments?: Record<string, unknown>;
}

export interface IToolFailedPayloadRaw {
  code?: string;
  message?: string;
}

export interface ISubagentPayloadRaw {
  subagent_id: string;
  label?: string;
  parent_subagent_id?: string | null;
  background?: boolean;
  observed?: boolean;
  supersedes_subagent_id?: string;
  superseded_by_subagent_id?: string;
}

export interface IArtifactCreatedPayloadRaw {
  artifact_id: string;
  message_id: string;
  placement: ArtifactPlacement;
  kind: ArtifactKind;
  content: Record<string, unknown>;
  client_message_id?: string;
}

export interface IConversationTitleUpdatedPayloadRaw {
  title: string;
}

export interface IRunCompletedPayloadRaw {
  response_message_id: string;
  usage?: Record<string, unknown>;
}

export interface IRunFailedPayloadRaw {
  code: string;
  message: string;
  retryable?: boolean;
}

export interface IRunInterruptedPayloadRaw {
  interrupt_id: string;
  reason?: string;
}

export interface IRunCancelledPayloadRaw {
  reason?: string;
}
/* eslint-enable @typescript-eslint/naming-convention */

export interface IAyaEnvelope {
  protocol: AyaStreamProtocol;
  sessionId: string;
  runId: string;
  messageId: string;
  toolCallId: string | null;
  seq: number;
  timestamp: string;
  type: string;
  payload: Record<string, unknown>;
}

export const toAyaEnvelope = (raw: IAyaEnvelopeRaw): IAyaEnvelope => ({
  protocol: raw.protocol,
  sessionId: raw.session_id,
  runId: raw.run_id,
  messageId: raw.message_id,
  toolCallId: raw.tool_call_id,
  seq: raw.seq,
  timestamp: raw.timestamp,
  type: raw.type,
  payload: raw.payload
});

/** Valida só o envelope. Cada consumidor valida o payload do seu tipo antes de adaptar. */
export const isAyaEnvelopeRaw = (value: unknown): value is IAyaEnvelopeRaw => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const raw = value as Partial<IAyaEnvelopeRaw>;
  return (
    raw.protocol === AYA_STREAM_PROTOCOL &&
    typeof raw.session_id === 'string' &&
    typeof raw.run_id === 'string' &&
    typeof raw.seq === 'number' &&
    Number.isInteger(raw.seq) &&
    raw.seq > 0 &&
    typeof raw.type === 'string' &&
    !!raw.payload &&
    typeof raw.payload === 'object'
  );
};

export const isTerminalEvent = (type: string): boolean => TERMINAL_EVENT_TYPES.includes(type);

export const toCursor = (runId: string, seq: number): string => `${runId}:${seq}`;

/**
 * Revisão do campo (estado ou título), persistida e compartilhada com o canal
 * do usuário. Não confundir com `envelope.seq`, que é a posição dentro da run.
 */
export const revisionOf = (envelope: IAyaEnvelope): number | undefined =>
  typeof envelope.payload.revision === 'number'
    ? envelope.payload.revision
    : undefined;
